import { ReactNode } from 'react';
import styled from 'styled-components';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { Dimensions, Metrics } from 'components/insights/constants';
import { formatPercentage, formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import { colors } from 'styles/theme/colors';

import { INBOUND_ENTITY_DIMENSIONS } from '../constants';

export type InboundReferralTableRow = {
  entityName: string;
  // The filter value that scopes a drill to this row: the group id for facility, else the text
  // value. Empty when the row has no name ("None") — those rows aren't clickable.
  entityId: string;
  referrals: number;
  acceptanceRate: number | null;
  denialRate: number | null;
  pendingRate: number | null;
  avgTimeToDecision: number | null;
};

export type InboundEntityCellRenderer = (info: CellContext<InboundReferralTableRow, string>) => ReactNode;

const CELL_PADDING = { meta: { padding: '16px 12px' } };
const FIRST_CELL_PADDING = { meta: { padding: '16px 12px 16px 24px' } };
const STRETCH_CELL = { meta: { padding: '16px 12px', style: { alignSelf: 'stretch' } } };

// Rows are the per-entity detail rows (grouping 0) for the single View-By dimension. When the
// dimension carries an id column (facility, via `:with_id`), the id sits at index 1 and becomes the
// row's entityId; otherwise the text name is its own id.
export function buildInboundRows(data: AnalyticsResponseData, dimension?: Dimensions): InboundReferralTableRow[] {
  const entityNames = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const hasIdColumn = dimension ? INBOUND_ENTITY_DIMENSIONS.has(dimension) : false;
  const entityIds = hasIdColumn ? data.dimensions.getCurrentDimensionValuesByIndex(1) : [];
  const referralCounts = data.metrics.getCurrentSeries(Metrics.REFERRAL_COUNT);
  const acceptanceRates = data.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);
  const denialRates = data.metrics.getCurrentSeries(Metrics.DENIAL_RATE);
  const pendingRates = data.metrics.getCurrentSeries(Metrics.PENDING_RATE);
  const decisionTimes = data.metrics.getCurrentSeries(Metrics.TIME_TO_FACILITY_DECISION_SECONDS_AVERAGE);

  return entityNames.map((entityName, i) => ({
    entityName: entityName || 'None',
    entityId: hasIdColumn ? (entityIds[i] ?? '') : (entityName ?? ''),
    referrals: referralCounts[i] ?? 0,
    acceptanceRate: acceptanceRates[i] ?? null,
    denialRate: denialRates[i] ?? null,
    pendingRate: pendingRates[i] ?? null,
    avgTimeToDecision: decisionTimes[i] ?? null,
  }));
}

const defaultEntityCell: InboundEntityCellRenderer = (info) => <EntityName>{info.getValue()}</EntityName>;

export function buildInboundColumns(
  entityHeader: string,
  entityCell: InboundEntityCellRenderer = defaultEntityCell
): ColumnDef<InboundReferralTableRow, any>[] {
  const helper = createColumnHelper<InboundReferralTableRow>();

  return [
    helper.accessor('entityName', {
      header: entityHeader,
      cell: entityCell,
      size: 320,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('referrals', {
      header: 'Total Referrals',
      cell: (info) => info.getValue(),
      size: 150,
      enableSorting: true,
      ...FIRST_CELL_PADDING,
    }),
    helper.accessor('acceptanceRate', {
      header: 'Acceptance Rate (%)',
      cell: (info) => <GreenText>{formatPercentage(info.getValue())}</GreenText>,
      size: 165,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('denialRate', {
      header: 'Denial Rate (%)',
      cell: (info) => <RedText>{formatPercentage(info.getValue())}</RedText>,
      size: 140,
      enableSorting: true,
      ...STRETCH_CELL,
    }),
    helper.accessor('pendingRate', {
      header: 'Pending Rate (%)',
      cell: (info) => formatPercentage(info.getValue()),
      size: 140,
      enableSorting: true,
      ...CELL_PADDING,
    }),
    helper.accessor('avgTimeToDecision', {
      header: 'Avg Time to Facility Decision',
      cell: (info) => formatResponseTime(info.getValue()),
      size: 230,
      enableSorting: true,
      ...CELL_PADDING,
    }),
  ];
}

const EntityName = styled.span`
  color: ${({ theme }) => theme.colors.black100};
  font-weight: var(--font-weight-medium);
`;

// Clickable entity name that opens the row's entity drill. Stretches to fill the cell so the whole
// first column is the hit target, matching the outbound referrals table.
export const EntityButton = styled.button`
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  display: block;
  width: calc(100% + 36px);
  margin: -16px -12px -16px -24px;
  padding: 16px 12px 16px 24px;
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: var(--font-weight-medium);
  white-space: normal;
  overflow-wrap: anywhere;

  td:hover & {
    text-decoration: underline;
  }
`;

const GreenText = styled.span`
  color: ${({ theme }) => theme.colors.green100};
  background-color: ${colors.green5};
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
`;

const RedText = styled.span`
  color: ${({ theme }) => theme.colors.red100};
  background-color: ${colors.red5};
  font-weight: var(--font-weight-medium);
  display: flex;
  align-items: center;
  align-self: stretch;
  margin: -16px -12px;
  padding: 16px 12px;
  flex: 1;
  width: 100%;
`;
